/**
 * Jira RAG service for querying Jira data
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import logger from './logger';
import { msalInstance } from '../index';

export interface JiraRequestPayload {
  question: string;
  chat_id: string;
}

export interface JiraResponse {
  chat_id: string;
  question: string;
  answer: string;
  logs: string[];
  ui_events: any[];
  metadata: {
    events_count: number;
    has_ui: boolean;
  };
}

export interface JiraApiResponse {
  statusCode: number;
  headers: {
    'Content-Type': string;
    'Access-Control-Allow-Origin': string;
    'Access-Control-Allow-Headers': string;
    'Access-Control-Allow-Methods': string;
  };
  body: string; // JSON string containing JiraResponse
}

/**
 * Get the Jira endpoint URL based on the environment
 */
function getJiraEndpoint(): string {
  const endpoint = process.env.REACT_APP_APIM_BASE_URL || 'https://navigator.ai.3m.com';
  return `${endpoint}/nav-rag-api/jira-rag`;
  // https://navigatordev.ai.3m.com/nav-rag-api/jira-rag
}
export class JiraChatService {
  private abortController: AbortController | null = null;

  /**
   * Get authentication token for API requests
   */
  private async getAuthToken(): Promise<string> {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error('No authenticated user found');
    }

    const request = {
      scopes: [`${process.env.REACT_APP_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`],
      account: accounts[0],
    };

    try {
      const response = await msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      logger.warn('[JIRA SERVICE] Silent token acquisition failed, trying popup:', error);
      // Fallback to interactive token acquisition
      const response = await msalInstance.acquireTokenPopup(request);
      return response.accessToken;
    }
  }

  /**
   * Send a query to the Jira RAG backend
   */
  public async sendQuery(question: string, sessionId?: string): Promise<JiraResponse> {
    const endpoint = getJiraEndpoint();
    
    try {
      // Create new abort controller for this request
      this.abortController = new AbortController();
      const abortController = this.abortController; // Store local reference

      logger.log('[JIRA SERVICE] Sending query:', {
        endpoint,
        question,
        sessionId,
      });

      // Get auth token
      const authToken = await this.getAuthToken();

      // Check if request was cancelled during auth
      if (!this.abortController || abortController !== this.abortController) {
        logger.log('[JIRA SERVICE] Query was cancelled before sending request (auth phase)');
        this.cancel();
        return Promise.reject(new Error('Query was cancelled'));
      }

      // Prepare the request payload - send directly as JSON (like Legal/DevOps/TEBG RAG)
      const requestBody: { question: string; chat_id?: string } = {
        question,
        chat_id: sessionId,
      };

      logger.log('[JIRA SERVICE] Request payload:', requestBody);

      // Add agent_log=all query parameter to get detailed logs
      const endpointWithParams = `${endpoint}?agent_log=all`;

      const response = await fetch(endpointWithParams, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(requestBody),
        signal: abortController.signal, // Use local reference
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      const responseData = JSON.parse(responseText);

      // Backend may return data in different formats:
      // 1. Wrapped format: { statusCode: 200, body: "..." }
      // 2. Direct format: { chat_id: "...", question: "...", answer: "..." }
      let jiraResponse: JiraResponse;
      
      if (responseData.body && typeof responseData.body === 'string') {
        // Wrapped format - parse the body string
        try {
          jiraResponse = JSON.parse(responseData.body);
        } catch (parseError) {
          throw new Error('Invalid response format from backend');
        }
      } else if (responseData.chat_id && responseData.answer) {
        // Direct format - use as-is
        jiraResponse = responseData as JiraResponse;
      } else {
        throw new Error('Unknown response format from backend');
      }
      
      logger.log('[JIRA] Parsed Jira response:', {
        chatId: jiraResponse.chat_id,
        question: jiraResponse.question,
        answerLength: jiraResponse.answer.length,
        logsCount: jiraResponse.logs.length,
        metadata: jiraResponse.metadata,
      });

      return jiraResponse;

    } catch (error: any) {
      if (error.name === 'AbortError') {
        logger.log('[JIRA] Query aborted by user');
        // Return a special object to indicate cancellation
        return { __cancelled: true } as any;
      } else {
        logger.error('[JIRA] ERROR querying Jira:', error);
        // Detect browser-level CORS / network failure (TypeError thrown before any HTTP
        // response is received — e.g. "Failed to fetch", "NetworkError when attempting
        // to fetch resource", "Load failed").
        const isCorsOrNetwork =
          error instanceof TypeError &&
          /(failed to fetch|networkerror|load failed)/i.test(error.message);
        if (isCorsOrNetwork) {
          throw new Error(
            'Your request could not be completed. Please try again or try rephrasing your prompt. ' +
            'If this keeps happening, please contact support.'
          );
        }
        throw new Error(error.message || 'Failed to query Jira. Please try again later.');
      }
    }
  }

  /**
   * Cancel the current query
   */
  public cancel(): void {
    if (this.abortController) {
      try {
        this.abortController.abort();
      } catch (err) {
        logger.warn('[JIRA SERVICE] Error during abort:', err);
      }
      this.abortController = null;
    }
  }

  /**
   * Cleanup resources
   */
  public dispose(): void {
    this.cancel();
  }
}
